import {
  getGoalCompletionPercentage,
  getTaskActionTarget,
} from '@griply/utils';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useSortedSmartLists } from 'features/smart-lists';
import { useSortedTags } from 'features/tags';
import React, { useMemo } from 'react';
import {
  FilterMinimalStyleCircle16,
  Heart,
  IconFormIcons,
  Tag,
  Target,
} from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import { TimeslotTitle } from 'shared/components/ui/timeslot-title';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { Task } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

export type ActionTimeslotTitleProps = {
  task: Task;
};

const GoalTimeslotTitle: React.FC<ActionTimeslotTitleProps> = ({ task }) => {
  const lifeAreas = useLocalizedLifeAreas();
  const links = useEntityLinks();
  const goals = useGoals();

  const target = useMemo(
    () => getTaskActionTarget(task.action, { goals }),
    [task.action, goals],
  );
  const goal =
    target?.type === TaskActionType.GoalTimeslot ? target.goal : undefined;

  const taskCounts = useTaskCounts();

  const completionPercentage = useMemo(
    () =>
      getGoalCompletionPercentage(goal?.id, goals, taskCounts?.byGoalProgress),
    [goal, goals, taskCounts?.byGoalProgress],
  );

  const color = useMemo(
    () =>
      goal
        ? getLinkedItemColor(goal.id, { goals, lifeAreas, links }) ?? undefined
        : undefined,
    [goal, goals, lifeAreas, links],
  );

  if (!goal) {
    return <TimeslotTitle title={task.name} icon={<Icon icon={Target} />} />;
  }

  return (
    <TimeslotTitle
      title={goal.name}
      color={color}
      icon={
        <CircleProgress
          percentage={completionPercentage}
          hidePercentage
          isSmall
        />
      }
      showChevron
    />
  );
};

const LifeAreaTimeslotTitle: React.FC<ActionTimeslotTitleProps> = ({
  task,
}) => {
  const lifeAreas = useLocalizedLifeAreas();

  const target = useMemo(
    () => getTaskActionTarget(task.action, { lifeAreas }),
    [task.action, lifeAreas],
  );
  const lifeArea =
    target?.type === TaskActionType.LifeAreaTimeslot
      ? target.lifeArea
      : undefined;

  if (!lifeArea) {
    return <TimeslotTitle title={task.name} icon={<Icon icon={Heart} />} />;
  }

  return (
    <TimeslotTitle
      title={lifeArea.name}
      color={lifeArea.color ?? undefined}
      icon={<Icon icon={IconFormIcons[lifeArea.iconName] ?? Heart} />}
      showChevron
    />
  );
};

const TagTimeslotTitle: React.FC<ActionTimeslotTitleProps> = ({ task }) => {
  const tags = useSortedTags();

  const target = useMemo(
    () => getTaskActionTarget(task.action, { tags }),
    [task.action, tags],
  );
  const tag =
    target?.type === TaskActionType.TagTimeslot ? target.tag : undefined;

  if (!tag) {
    return <TimeslotTitle title={task.name} icon={<Icon icon={Tag} />} />;
  }

  return (
    <TimeslotTitle
      title={tag.name}
      color={tag.color}
      icon={<Icon icon={Tag} />}
      showChevron
    />
  );
};

const SmartListTimeslotTitle: React.FC<ActionTimeslotTitleProps> = ({
  task,
}) => {
  const smartLists = useSortedSmartLists();

  const target = useMemo(
    () => getTaskActionTarget(task.action, { smartLists }),
    [task.action, smartLists],
  );
  const smartList =
    target?.type === TaskActionType.SmartListTimeslot
      ? target.smartList
      : undefined;

  if (!smartList) {
    return (
      <TimeslotTitle
        title={task.name}
        icon={<Icon icon={FilterMinimalStyleCircle16} />}
      />
    );
  }

  return (
    <TimeslotTitle
      title={smartList.name}
      color={smartList.color ?? undefined}
      icon={
        <Icon
          icon={
            smartList.icon
              ? IconFormIcons[smartList.icon]
              : FilterMinimalStyleCircle16
          }
        />
      }
      showChevron
    />
  );
};

export const ActionTimeslotTitle: React.FC<ActionTimeslotTitleProps> = ({
  task,
}) => {
  if (task.action?.type === TaskActionType.GoalTimeslot) {
    return <GoalTimeslotTitle task={task} />;
  }
  if (task.action?.type === TaskActionType.LifeAreaTimeslot) {
    return <LifeAreaTimeslotTitle task={task} />;
  }
  if (task.action?.type === TaskActionType.TagTimeslot) {
    return <TagTimeslotTitle task={task} />;
  }
  if (task.action?.type === TaskActionType.SmartListTimeslot) {
    return <SmartListTimeslotTitle task={task} />;
  }

  return <TimeslotTitle title={task.name} />;
};
